import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaHome, FaSearch, FaSitemap, FaEnvelope, FaPhoneAlt } from 'react-icons/fa';
import { 
  Button, 
  Card, 
  CardBody, 
  CardFooter,
  Input,
  Chip 
} from "@heroui/react";

const NotFound = () => {
  const navigate = useNavigate();
  
  // Automatyczne przekierowanie na stronę główną po 60 sekundach
  useEffect(() => {
    const redirectTimer = setTimeout(() => {
      navigate('/');
    }, 60000); // 60 sekund
    
    return () => clearTimeout(redirectTimer);
  }, [navigate]);
  
  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 50, damping: 20 }
    }
  };
  
  // Popularne linki
  const popularLinks = [
    { name: 'Strona główna', path: '/', icon: <FaHome /> },
    { name: 'Usługi', path: '/uslugi', icon: <FaSitemap /> },
    { name: 'Kontakt', path: '/kontakt', icon: <FaEnvelope /> },
    { name: 'Portfolio', path: '/portfolio', icon: <FaSearch /> }
  ];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 overflow-hidden relative">
      {/* Elementy tła */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary" />
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-primary" />
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full filter blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary/10 rounded-full filter blur-3xl" />
      
      <div className="relative w-full max-w-4xl z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center mb-10"
        >
          <motion.div 
            variants={itemVariants}
            className="inline-block mb-6"
          >
            <div className="h-40 flex items-center justify-center">
              <div className="relative">
                <motion.div
                  animate={{ 
                    y: [0, -10, 0], 
                    rotate: [0, 2, 0, -2, 0],
                    scale: [1, 1.02, 1]
                  }}
                  transition={{ 
                    duration: 5, 
                    repeat: Infinity,
                    repeatType: 'loop'
                  }}
                  className="text-9xl font-extrabold text-transparent bg-clip-text bg-gradient-to-br from-primary to-secondary"
                >
                  404
                </motion.div>
                <motion.div
                  animate={{ 
                    opacity: [0.5, 0.8, 0.5],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity,
                    repeatType: 'loop'
                  }}
                  className="absolute -bottom-4 left-0 right-0 h-2 bg-gradient-to-r from-primary/40 to-secondary/40 blur-xl"
                />
              </div>
            </div>
          </motion.div>
          
          <motion.h1 
            variants={itemVariants}
            className="text-3xl md:text-4xl font-bold mb-4"
          >
            Strona nie została znaleziona
          </motion.h1>
          
          <motion.p 
            variants={itemVariants}
            className="text-foreground-muted max-w-lg mx-auto mb-8"
          >
            Przepraszamy, ale strona, której szukasz, nie istnieje lub została przeniesiona.
            Sprawdź, czy adres URL jest poprawny lub wybierz jedną z poniższych opcji.
          </motion.p>
        </motion.div>
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {/* Wyszukiwarka */}
          <motion.div
            variants={itemVariants}
            className="md:col-span-2"
          >
            <Card className="shadow-md" isHoverable>
              <CardBody>
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <FaSearch className="mr-2 text-primary" /> 
                  Wyszukaj na stronie
                </h2>
                <p className="text-foreground-muted text-sm mb-4">
                  Spróbuj znaleźć to, czego szukasz, używając wyszukiwarki:
                </p>
                <div className="flex">
                  <Input
                    placeholder="Wpisz szukaną frazę..."
                    className="rounded-r-none"
                    size="lg"
                  />
                  <Button
                    color="primary"
                    className="rounded-l-none"
                    size="lg"
                  >
                    Szukaj
                  </Button>
                </div>
              </CardBody>
              <CardFooter>
                <div className="flex flex-wrap gap-2 w-full">
                  <span className="text-sm text-foreground-muted">Popularne wyszukiwania:</span>
                  <Chip variant="flat" color="primary" size="sm">Infrastruktura</Chip>
                  <Chip variant="flat" color="secondary" size="sm">Cloud</Chip>
                  <Chip variant="flat" color="success" size="sm">Bezpieczeństwo</Chip>
                  <Chip variant="flat" color="warning" size="sm">Aplikacje</Chip>
                </div>
              </CardFooter>
            </Card>
          </motion.div>
          
          {/* Popularne linki */}
          <motion.div
            variants={itemVariants}
          >
            <Card className="shadow-md h-full" isHoverable>
              <CardBody className="h-full flex flex-col">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <FaSitemap className="mr-2 text-primary" />
                  Popularne strony
                </h2>
                <p className="text-foreground-muted text-sm mb-4">
                  Przejdź do jednej z najczęściej odwiedzanych sekcji naszej strony:
                </p>
                <div className="space-y-3 flex-grow">
                  {popularLinks.map((link, index) => (
                    <Link
                      key={index}
                      to={link.path}
                      className="flex items-center p-3 rounded-md hover:bg-default-100 transition-colors w-full"
                    >
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary mr-3">
                        {link.icon}
                      </span>
                      <span>{link.name}</span>
                    </Link>
                  ))}
                </div>
                
                <div className="mt-auto pt-4">
                  <Button 
                    as={Link}
                    to="/"
                    color="primary"
                    variant="flat"
                    fullWidth
                    startContent={<FaHome />}
                  >
                    Wróć do strony głównej
                  </Button>
                </div>
              </CardBody>
            </Card>
          </motion.div>
        </motion.div>
        
        {/* Pomoc i kontakt */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          className="mt-6"
        >
          <Card className="shadow-md bg-gradient-to-r from-primary/5 to-secondary/5 border border-primary/10">
            <CardBody>
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold mb-2">Potrzebujesz pomocy?</h2>
                  <p className="text-foreground-muted">
                    Nasz zespół wsparcia jest dostępny, aby pomóc Ci znaleźć to, czego szukasz.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    as={Link}
                    to="/kontakt"
                    color="primary"
                    startContent={<FaEnvelope />}
                  >
                    Formularz kontaktowy
                  </Button>
                  <Button
                    as="a"
                    href="tel:+48690481963"
                    variant="bordered"
                    color="primary"
                    startContent={<FaPhoneAlt />}
                  >
                    +48 690 481 963
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>
        
        {/* Informacja o automatycznym przekierowaniu */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="text-center mt-8"
        >
          <p className="text-foreground-muted text-sm">
            Zostaniesz automatycznie przekierowany na stronę główną za 60 sekund.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFound;
